import type { NodeWithPos } from '@tiptap/core';
import type { Node } from '@tiptap/pm/model';
import type { Transaction } from '@tiptap/pm/state';
import type { Editor } from '@tiptap/react';
import type { Node as ProseMirrorNode } from 'prosemirror-model';

import { generateId, TIPTAP_LAYOUT_CELL_TYPE, TIPTAP_LAYOUT_TYPE } from './../../ricos-content/index.js';
import { Node_Type } from './../../ricos-schema/legacy/wix/rich_content/v1/index.js';
import { TOOLBARS } from './../../editor-common/index.js';

export const MAX_COLUMNS = 12;
export const MAX_CELLS = 3;
export const MIN_COL_SPAN = 3;

export const createEmptyCell = (colSpan: number) => ({
  type: TIPTAP_LAYOUT_CELL_TYPE,
  attrs: {
    id: generateId(),
    colSpan,
  },
  content: [
    {
      type: Node_Type.PARAGRAPH,
      content: [],
    },
  ],
});

export const balanceLayoutCells = (tr: Transaction, node: Node, pos: number) => {
  const colSpan = MAX_COLUMNS / Math.min(node?.childCount ?? MAX_CELLS, MAX_CELLS);

  node?.forEach((_child, childPos) => {
    tr.setNodeAttribute(pos + childPos + 1, 'colSpan', colSpan);
  });
};

export const findLayoutOfCell = (node: ProseMirrorNode, cellId: string): NodeWithPos | null => {
  let maybeLayoutNode: NodeWithPos | null = null;

  node.descendants((child, pos) => {
    if (child.type.name === TIPTAP_LAYOUT_TYPE) {
      for (let i = 0; i < child.childCount; i++) {
        if (child.child(i).attrs.id === cellId) {
          maybeLayoutNode = { node: child, pos };
          break;
        }
      }
    }
  });

  return maybeLayoutNode;
};

export const getHandleSmallIcon = (className?: string) =>
  `<svg data-drag-handle="" class="${className}" viewBox="0 0 18 18" fill="currentColor" width="18" height="18"><path d="M6 5a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm4 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0ZM6 9a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm4 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm-4 4a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm4 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0Z"></path></svg>`;

export const getToolbarReferenceElement = (tiptapEditor: Editor, handleSelector: string) => {
  const startNodePos = tiptapEditor.state.selection.$from;
  const nodeElement = tiptapEditor.view.nodeDOM(startNodePos.pos) as HTMLElement | null;

  const handleElement = (
    tiptapEditor.view.nodeDOM(startNodePos.pos) as HTMLElement | null
  )?.querySelector?.(handleSelector);

  if (nodeElement && handleElement) {
    return {
      getBoundingClientRect: () => {
        const nodeElementRect = nodeElement?.getBoundingClientRect();
        const handleElementRect = handleElement?.getBoundingClientRect();
        return new DOMRect(
          nodeElementRect.x,
          handleElementRect.y,
          nodeElementRect.width,
          nodeElementRect.height + handleElementRect.height
        );
      },
      contextElement: nodeElement,
    };
  }

  return tiptapEditor.view.nodeDOM(startNodePos.pos - 1) as HTMLElement | null;
};

export const toolbarsToAddIn = [TOOLBARS.FOOTER, TOOLBARS.SIDE, TOOLBARS.MOBILE];

export type LayoutEditorConfig = {
  showInToolbars: boolean;
  showInQuickActions: boolean;
};
